import { Button, Modal, notification, Tooltip } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent } from "vue";
import { setIsLoading as setIsLoadingApp } from "../../../../../app.logic";
import {
    createFieldInput,
    createFieldRadioGroup,
    createFieldSelect
} from "../../../../../components/input/template-builder/template-builder.logic";
import { createColumnDate, createColumnInput, createColumnSelect } from "../../../../../components/table/c-table.logic";
import i18n from "../../../../../i18n";
import router from "../../../../../router";
import store from "../../../../../store";
import { download } from "../../../../../utils/file";
import { conditionFormChecker, copyForm, putField } from "../../../../../utils/form";
import { callCheckIdCard, callCheckIdTax, callPersonType } from "../../../../account/register/register.section";
import { callTitle } from "../../../setting/user-mgt/user/user.section";
import { createFieldDesc } from "../tab-1/tab1.section";
import { callDocumentTypePerson, callUserRequestStatus, callUserRequestType } from "../tab-4/tab4.section";

const AttachmentInlineList = defineAsyncComponent(() => import("../../../../../components/attachment/AttachmentInlineList"));
const CIcon = defineAsyncComponent(() => import("../../../../../components/icon/CIcon"));
const CSelect = defineAsyncComponent(() => import("../../../../../components/input/template-builder/select/CSelect"));

export const callUserRequestDoc5Attachment = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "USER_REQUEST_DOC_5_ATTACHMENT", value: searchQuery });
export const callUserAuthorize = ({ searchQuery, parentUsername, authorizeTypeKey }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "isTemp", type: "Boolean", operator: "=", value: false },
        { name: "refAuthorizeType.key", type: "String", operator: "=", value: authorizeTypeKey },
        { name: "refUser.username", type: "String", operator: "=", value: parentUsername }
    ];
    if (searchQuery) {
        where.push(
            { name: "companyName", type: "String", operator: "like", value: searchQuery },
            { logic: "OR", name: "fullName", type: "String", operator: "like", value: searchQuery }
        );
    }
    return store
        .dispatch("resource/v1/transaction/userAuthorize/search", {
            criteria: {
                select: ["id", "refPersonType.key", "companyName", "fullName"],
                page: 0,
                size: 1000000,
                where,
                sort: [
                    { name: "companyName", type: "ASC" },
                    { name: "fullName", type: "ASC" }
                ],
                children: []
            }
        })
        .then(page => page.data);
};
export const callUserAuthorizeFull = ({ id }) => {
    return store
        .dispatch("resource/v1/transaction/userAuthorize/search", {
            criteria: {
                select: ["**"],
                page: 0,
                size: 1,
                where: [{ name: "id", type: "UUID", operator: "=", value: id }],
                children: [
                    { name: "roles", select: ["**"], page: 0, size: 1000000 },
                    { name: "attachments", select: ["**"], page: 0, size: 1000000, sort: [{ name: "createDate", type: "ASC" }] }
                ]
            }
        })
        .then(page => page.data[0]);
};

export const createColumns = () => {
    return [
        createColumnInput({
            title: () => i18n.global.t("views.company.mgt.tab.5.title"),
            dataIndex: "refUserAuthorizeTemp.companyName",
            dataIndexValue: ({ record }) =>
                record.refUserAuthorizeTemp.refPersonType?.key === "1"
                    ? record.refUserAuthorizeTemp.companyName
                    : record.refUserAuthorizeTemp.fullName,
            customRender: ({ record, column }) => column.dataIndexValue({ record }),
            align: "center"
        }),
        createColumnInput({
            title: () => i18n.global.t("views.company.mgt.tab.5.docId"),
            dataIndex: "refUserAuthorizeTemp.idCard",
            dataIndexValue: ({ record }) =>
                record.refUserAuthorizeTemp.refPersonType?.key === "1"
                    ? `${i18n.global.t("views.register.idTax")} ${record.refUserAuthorizeTemp.idCard}`
                    : `${record.refUserAuthorizeTemp.refDocumentTypePerson?.value} ${record.refUserAuthorizeTemp.idCard}`,
            customRender: ({ record }) => {
                if (record.refUserAuthorizeTemp.refPersonType?.key === "1") {
                    return (
                        <div>
                            <div>{i18n.global.t("views.register.idTax")}</div>
                            <div>{record.refUserAuthorizeTemp.idCard}</div>
                        </div>
                    );
                } else if (record.refUserAuthorizeTemp.refPersonType?.key === "2") {
                    return (
                        <div>
                            <div>{record.refUserAuthorizeTemp.refDocumentTypePerson?.value}</div>
                            <div>{record.refUserAuthorizeTemp.idCard}</div>
                        </div>
                    );
                }
                return <div></div>;
            },
            align: "center"
        }),
        createColumnDate({
            title: () => i18n.global.t("views.company.mgt.tab.approveDate"),
            dataIndex: "lastStatus.approve.0.statusDatetime",
            align: "center",
            width: 130
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.userRequest.refRequestType"),
            dataIndex: "refRequestType.value",
            inputProp: () => ({ list: callUserRequestType, optLabel: "value" }),
            search: { name: "refRequestType.id" },
            align: "center",
            width: 110
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.userRequest.refStatus"),
            dataIndex: "refStatus.value",
            inputProp: () => ({ list: callUserRequestStatus, optLabel: "value" }),
            search: { name: "refStatus.id" },
            align: "center",
            width: 100
        })
    ];
};

export const createField = ({ props, data, refs, key, isLoading }) => {
    const { viewOnly, menuContext } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            ...createFieldDesc({ props, data, key, isLoading }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refRequestType`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.userRequest.refRequestType"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refRequestType,
                            "onUpdate:value": value => {
                                const oldValue = model.refRequestType;
                                model.refRequestType = value;
                                if (
                                    (["2"].includes(oldValue?.key) || (!["1"].includes(oldValue?.key) && ["1"].includes(value?.key))) &&
                                    model.refUserAuthorize
                                ) {
                                    Modal.confirm({
                                        title: i18n.global.t("views.request.modal.change", {
                                            text: i18n.global.t("model.userRequest.refRequestType")
                                        }),
                                        autoFocusButton: "cancel",
                                        onOk: () => {
                                            if (["1"].includes(value?.key)) {
                                                const { createForm } = require("./tab5.logic");
                                                cloneUserAuthorize({
                                                    model: {
                                                        ...createForm({ props, data, refs }),
                                                        id: model.id,
                                                        version: model.version,
                                                        isActive: model.isActive,
                                                        refStatus: model.refStatus,
                                                        refRequestDoc: model.refRequestDoc
                                                    }
                                                });
                                            } else {
                                                cloneUserAuthorize({ model, id: model.refUserAuthorize?.id });
                                            }
                                        },
                                        onCancel: () => {
                                            model.refRequestType = oldValue;
                                        }
                                    });
                                }
                            },
                            disabled: viewOnly,
                            list: callUserRequestType,
                            optLabel: "value",
                            allowClear: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refUserAuthorize`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.company.mgt.tab.5.title"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refUserAuthorize,
                            "onUpdate:value": value => {
                                const oldValue = model.refUserAuthorize;
                                model.refUserAuthorize = value;
                                if (oldValue) {
                                    Modal.confirm({
                                        title: i18n.global.t("views.request.modal.change", { text: i18n.global.t("views.company.mgt.tab.5.title") }),
                                        autoFocusButton: "cancel",
                                        onOk: () => cloneUserAuthorize({ model, id: value?.id }),
                                        onCancel: () => {
                                            model.refUser = oldValue;
                                        }
                                    });
                                } else {
                                    cloneUserAuthorize({ model, id: value?.id });
                                }
                            },
                            disabled: viewOnly,
                            list: ({ searchQuery }) =>
                                callUserAuthorize({
                                    searchQuery,
                                    parentUsername: store.state.resource.auth.info.user.username,
                                    authorizeTypeKey: "1"
                                }),
                            renderLabel: ({ item }) => {
                                if (item.refPersonType?.key === "1") {
                                    return <div>{item.companyName}</div>;
                                } else if (item.refPersonType?.key === "2") {
                                    return <div>{item.fullName}</div>;
                                }
                                return <div></div>;
                            },
                            allowClear: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.fullNameTh`) }
            }),
            ...createFieldUserAuthorize({
                props,
                data,
                key: `${prefix}refUserAuthorizeTemp`,
                isLoading,
                onLinkIdCard: onLinkIdCard({ props, data, refs, isLoading })
            }),
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                inputRender: () => {
                    const componentProp = lodash.merge(
                        {
                            menuContext: menuContext,
                            viewOnly: viewOnly,
                            dataProp: data,
                            itemsKey: `${prefix}refUserAuthorizeTemp.attachments`,
                            canCreate: !viewOnly,
                            canDelete: !viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { componentKey: "AttachmentInlineList" } })
                    );
                    return <AttachmentInlineList {...componentProp}></AttachmentInlineList>;
                }
            }
        ]
    });
};

export const createFieldUserAuthorize = ({ props, data, key /* , isLoading */, onLinkIdCard }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldRadioGroup({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refPersonType`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.userAuthorize.refPersonType"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refPersonType,
                            "onUpdate:value": value => (model.refPersonType = value),
                            disabled: viewOnly,
                            list: callPersonType,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}companyName`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.userAuthorize.companyName"),
                inputRender: ({ field }) => {
                    const placeholder = i18n.global.t("model.userAuthorize.companyName");
                    const inputProp = lodash.merge(
                        {
                            value: model.companyName,
                            "onUpdate:value": value => (model.companyName = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (model.companyName && data.linkIdCard?.companyNameTh?.length >= 0 && model.companyName !== data.linkIdCard.companyNameTh) {
                        linkCompare = (
                            <>
                                {model.companyName}
                                <span className="text-danger">==&gt; {data.linkIdCard.companyNameTh}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <Input placeholder={placeholder} {...inputProp}></Input>
                            {linkCompare}
                        </>
                    );
                }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refTitle`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.user.refTitle"),
                inputRender: ({ field }) => {
                    const placeholder = i18n.global.t("model.user.refTitle");
                    const inputProp = lodash.merge(
                        {
                            value: model.refTitle,
                            "onUpdate:value": value => (model.refTitle = value),
                            disabled: viewOnly,
                            list: callTitle,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (model.refTitle && data.linkIdCard?.titleName?.length >= 0 && model.refTitle.value !== data.linkIdCard.titleName) {
                        linkCompare = (
                            <>
                                {model.refTitle.value}
                                <span className="text-danger">==&gt; {data.linkIdCard.titleName}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <CSelect placeholder={placeholder} {...inputProp}></CSelect>
                            {linkCompare}
                        </>
                    );
                },
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}firstName`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.userAuthorize.firstName"),
                inputRender: ({ field }) => {
                    const placeholder = i18n.global.t("model.userAuthorize.firstName");
                    const inputProp = lodash.merge(
                        {
                            value: model.firstName,
                            "onUpdate:value": value => (model.firstName = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (model.firstName && data.linkIdCard?.firstNameTh?.length >= 0 && model.firstName !== data.linkIdCard.firstNameTh) {
                        linkCompare = (
                            <>
                                {model.firstName}
                                <span className="text-danger">==&gt; {data.linkIdCard.firstNameTh}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <Input placeholder={placeholder} {...inputProp}></Input>
                            {linkCompare}
                        </>
                    );
                }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}lastName`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.userAuthorize.lastName"),
                inputRender: ({ field }) => {
                    const placeholder = i18n.global.t("model.userAuthorize.lastName");
                    const inputProp = lodash.merge(
                        {
                            value: model.lastName,
                            "onUpdate:value": value => (model.lastName = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                    let linkCompare = <div></div>;
                    if (model.lastName && data.linkIdCard?.firstNameTh?.length >= 0 && model.lastName !== data.linkIdCard.firstNameTh) {
                        linkCompare = (
                            <>
                                {model.lastName}
                                <span className="text-danger">==&gt; {data.linkIdCard.firstNameTh}</span>
                            </>
                        );
                    }
                    return (
                        <>
                            <Input placeholder={placeholder} {...inputProp}></Input>
                            {linkCompare}
                        </>
                    );
                }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refDocumentTypePerson`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.userAuthorize.refDocumentTypePerson"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refDocumentTypePerson,
                            "onUpdate:value": value => (model.refDocumentTypePerson = value),
                            disabled: viewOnly,
                            list: callDocumentTypePerson,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}idCard`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () =>
                    ["1"].includes(model.refPersonType?.key) ? i18n.global.t("views.register.idTax") : i18n.global.t("model.userAuthorize.idCard"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.idCard,
                            "onUpdate:value": value => (model.idCard = value),
                            disabled: viewOnly,
                            comma: false,
                            onBlur: () => useForm.validate(field.key).catch(() => {}),
                            suffix: () => {
                                const forceNoShow = true;
                                if (forceNoShow || !data.linkIdCard?.idCard) {
                                    return null;
                                }
                                let icon = <CIcon class="text-success" file="check.svg"></CIcon>;
                                if (data.linkIdCard?.idCard !== model.idCard) {
                                    icon = <CIcon class="text-danger" file="close.svg"></CIcon>;
                                }
                                return <Tooltip title={data.linkIdCard?.idCard}>{icon}</Tooltip>;
                            }
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            {
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}buttonCheckIdCard`,
                colProp: () => ({ md: 4 }),
                formItemRender: () => {
                    let manual = <div></div>;
                    if (["2"].includes(model.refPersonType?.key)) {
                        manual = (
                            <a
                                class="text-center"
                                href="javascript:void(0)"
                                onClick={() =>
                                    download({
                                        file: { name: i18n.global.t("views.register.linkageClient"), url: "/files/server/LinkageClient.exe" }
                                    })
                                }
                            >
                                ({i18n.global.t("views.register.downloadLinkageClient")})
                            </a>
                        );
                    }
                    return (
                        <>
                            <div>
                                <Button
                                    class="mt-auto mb-auto btn-primary"
                                    onClick={() => {
                                        setIsLoadingApp(true);
                                        if (["1"].includes(model.refPersonType?.key)) {
                                            callCheckIdTax({ idTax: model.idCard })
                                                .then(res => {
                                                    //console.log(res);
                                                    if (!res) return null;
                                                    model.isVerify = true;
                                                    const resultList = res?.ResultList?.[0];
                                                    return {
                                                        idCard: resultList.JuristicID,
                                                        companyNameTh: resultList.JuristicName_TH,
                                                        companyNameEn: resultList.JuristicName_EN,
                                                        companyType: resultList.JuristicType,
                                                        addresses: resultList.AddressInformations.map(d => ({
                                                            name: d.AddressName,
                                                            addrTh: d.FullAddress,
                                                            province: d.Province,
                                                            amphur: d.Ampur,
                                                            tumbol: d.Tumbol,
                                                            tel: d.Phone
                                                        }))
                                                    };
                                                })
                                                .then(d => onLinkIdCard?.((data.linkIdCard = d)))
                                                .finally(() => setIsLoadingApp(false));
                                        } else if (["2"].includes(model.refPersonType?.key)) {
                                            callCheckIdCard({ idCard: model.idCard }).finally(() => {
                                                setTimeout(() => {
                                                    setIsLoadingApp(false);
                                                    if (!data.linkageReceive001 || !data.linkageReceive009) {
                                                        notification["error"]({ message: i18n.global.t("error.linkage.noResponse") });
                                                    }
                                                }, 10000);
                                            });
                                        }
                                    }}
                                >
                                    {["1"].includes(model.refPersonType?.key)
                                        ? i18n.global.t("views.register.buttonCheckIdTax")
                                        : i18n.global.t("views.register.buttonCheckIdCard")}
                                </Button>
                            </div>
                            {manual}
                        </>
                    );
                }
            }
        ]
    });
};

export const cloneUserAuthorize = ({ model, id }) => {
    if (id) {
        callUserAuthorizeFull({ id }).then(user => {
            const refUserAuthorizeTemp = copyForm({ form: { ...user } });
            router.push({
                name: router.currentRoute.value.name,
                params: { props: JSON.stringify({ form: { ...model, refUserAuthorizeTemp } }) },
                query: { _timestamp: new Date().getTime() }
            });
        });
    } else {
        router.push({
            name: router.currentRoute.value.name,
            params: { props: JSON.stringify({ forceDefault: true, form: model }) },
            query: { _timestamp: new Date().getTime() }
        });
    }
};

export const onLinkIdCard = ({ data, key }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    return d => {
        const result = [];
        if (model.refUserAuthorizeTemp.refPersonType?.key === "1") {
            if (
                !(
                    (model.refUserAuthorizeTemp.companyName || "") === (d?.companyName || "") ||
                    (model.refUserAuthorizeTemp.companyName || "") === (d?.companyNameTh || "") ||
                    (model.refUserAuthorizeTemp.companyName || "") === (d?.companyNameEn || "")
                )
            )
                result.push(
                    i18n.global.t("views.register.linkWrong", {
                        text: i18n.global.t("model.userAuthorize.companyName")
                    })
                );
            if ((model.refUserAuthorizeTemp.idCard || "") !== (d?.idCard || ""))
                result.push(
                    i18n.global.t("views.register.linkWrong", {
                        text: i18n.global.t("views.register.idTax")
                    })
                );
        } else if (model.refUserAuthorizeTemp.refPersonType?.key === "2") {
            if ((model.refUserAuthorizeTemp.idCard || "") !== (d?.idCard || ""))
                result.push(
                    i18n.global.t("views.register.linkWrong", {
                        text: i18n.global.t("views.register.idCard")
                    })
                );
            if ((model.refUserAuthorizeTemp.refTitle?.value || "") !== (d?.titleName || ""))
                result.push(
                    i18n.global.t("views.register.linkWrong", {
                        text: i18n.global.t("model.userAuthorize.refTitle")
                    })
                );
            if ((model.refUserAuthorizeTemp.firstName || "") !== (d?.firstNameTh || ""))
                result.push(
                    i18n.global.t("views.register.linkWrong", {
                        text: i18n.global.t("model.userAuthorize.firstName")
                    })
                );
            if ((model.refUserAuthorizeTemp.lastName || "") !== (d?.lastNameTh || ""))
                result.push(
                    i18n.global.t("views.register.linkWrong", {
                        text: i18n.global.t("model.userAuthorize.lastName")
                    })
                );
        }
        model.lastStatus.forOfficeRemark[0].remark = result.join("\r\n");
    };
};
